import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Field } from '@/components/ui/Field/Field';
import { Icon } from '@/components/ui/Icon/Icon';
import { ApiRequestError } from '@/services/api';
import { compressImageForUpload } from '@/utils/compressImage';
import { useCreateVedlikeholdEntry, useUpdateVedlikeholdEntry } from '@/services/vedlikehold';
import { useCreateKategori } from '@/services/kategorier';
import { nb } from '@/i18n/nb';
import type { VedlikeholdEntry, VedlikeholdKategori, VedlikeholdSection } from '@/types/api';
import s from './EntryFormModal.module.css';

type CommonProps = {
  // Which sub-card this form serves. `generelt` shows the kategori picker + requires
  // a kategori; `dugnad` is a flat list (ADR-0028) so the kategori UI is hidden and
  // kategoriId is sent as null. Everything else (description, optional image) is shared.
  section: VedlikeholdSection;
  kategorier: VedlikeholdKategori[];
  onClose: () => void;
};

type AddProps = CommonProps & {
  mode: 'add';
};

type EditProps = CommonProps & {
  mode: 'edit';
  entry: VedlikeholdEntry;
};

type Props = AddProps | EditProps;

/*
 * EntryFormModal — add OR edit a Vedlikehold entry, for BOTH sub-cards. One
 * component, two modes (add/edit) × two sections (generelt/dugnad). The delta
 * between sections is just the kategori picker, so a `section` prop gates it rather
 * than duplicating the whole form for Dugnadsoppgaver (ADR-0028 — "same structure as
 * Generelt minus kategori").
 *
 * Fields:
 *   - Kategori dropdown + inline "+ Ny kategori" — GENERELT ONLY (dugnad is flat)
 *   - Description textarea, 1–1024 chars
 *   - Optional image upload (separable attachment, NOT bound to the description —
 *     contrast with Galleri where image+caption are inseparable)
 *
 * Edit mode additions:
 *   - Existing image is shown with [Erstatt] (replace) + [Fjern] (detach) actions
 *   - "kan flyttes" hint on kategori label (generelt only)
 */
export function EntryFormModal(props: Props) {
  const isEdit = props.mode === 'edit';
  const isDugnad = props.section === 'dugnad';
  const initialKategoriId = isEdit ? (props.entry.kategoriId ?? '') : '';
  const initialDescription = isEdit ? props.entry.description : '';
  const initialImageUrl = isEdit ? (props.entry.image?.thumbUrl ?? null) : null;

  // Form state
  const [kategoriId, setKategoriId] = useState<string>(initialKategoriId);
  const [description, setDescription] = useState<string>(initialDescription);
  const [newImage, setNewImage] = useState<File | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState<boolean>(false);
  // True while a picked photo is decoded + re-encoded (ADR-0030) — gates submit.
  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ kategori?: string; description?: string; image?: string }>(
    {},
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Inline "+ Ny kategori" UI
  const [newKategoriOpen, setNewKategoriOpen] = useState<boolean>(false);
  const [newKategoriName, setNewKategoriName] = useState<string>('');
  const [newKategoriError, setNewKategoriError] = useState<string | null>(null);
  const newKategoriInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateVedlikeholdEntry();
  const updateMutation = useUpdateVedlikeholdEntry();
  const createKategoriMutation = useCreateKategori();

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isCreatingKategori = createKategoriMutation.isPending;

  // Focus the inline kategori input as soon as it opens — keyboard users
  // shouldn't have to tab around to type the new name.
  useEffect(() => {
    if (newKategoriOpen) newKategoriInputRef.current?.focus();
  }, [newKategoriOpen]);

  function validate(): boolean {
    const next: typeof errors = {};
    if (!isDugnad && !kategoriId)
      next.kategori = nb.vedlikehold.addModal.validation.kategoriRequired;
    const trimmed = description.trim();
    if (trimmed.length === 0)
      next.description = nb.vedlikehold.addModal.validation.descriptionRequired;
    else if (trimmed.length > 1024)
      next.description = nb.vedlikehold.addModal.validation.descriptionMax;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  // Compress oversized picks before they land in state (ADR-0030) — same flow as
  // the Galleri upload modal; the preview + eventual POST see the prepared file.
  async function pickImage(file: File | null) {
    if (!file) {
      setNewImage(null);
      return;
    }
    setIsPreparing(true);
    try {
      setNewImage(await compressImageForUpload(file));
      setRemoveExistingImage(false);
    } finally {
      setIsPreparing(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    if (isPreparing || !validate()) return;

    if (props.mode === 'add') {
      createMutation.mutate(
        {
          section: props.section,
          // Dugnad is a flat list — never sends a kategori (ADR-0028).
          kategoriId: isDugnad ? null : kategoriId,
          description: description.trim(),
          image: newImage,
        },
        {
          onSuccess: () => props.onClose(),
          onError: (e) =>
            setSubmitError(e instanceof Error ? e.message : nb.vedlikehold.addModal.errorTitle),
        },
      );
    } else {
      updateMutation.mutate(
        {
          id: props.entry.id,
          section: props.section,
          // kategori is generelt-only; for dugnad it stays undefined (never sent).
          kategoriId: !isDugnad && kategoriId !== initialKategoriId ? kategoriId : undefined,
          description: description !== initialDescription ? description.trim() : undefined,
          image: newImage ?? undefined,
          removeImage: removeExistingImage,
        },
        {
          onSuccess: () => props.onClose(),
          onError: (e) =>
            setSubmitError(e instanceof Error ? e.message : nb.vedlikehold.addModal.errorTitle),
        },
      );
    }
  }

  function handleCreateNewKategori() {
    const name = newKategoriName.trim();
    setNewKategoriError(null);
    if (name.length === 0) {
      setNewKategoriError(nb.vedlikehold.addModal.validation.kategoriNameRequired);
      return;
    }
    if (name.length > 64) {
      setNewKategoriError(nb.vedlikehold.addModal.validation.kategoriNameMax);
      return;
    }
    createKategoriMutation.mutate(name, {
      onSuccess: (response) => {
        // Select the newly-created kategori automatically.
        setKategoriId(response.data.id);
        setNewKategoriOpen(false);
        setNewKategoriName('');
      },
      onError: (e) => {
        setNewKategoriError(
          e instanceof ApiRequestError ? e.message : nb.vedlikehold.addModal.errorTitle,
        );
      },
    });
  }

  // The displayed image: removed → none; replaced → preview the new File; else
  // → the existing thumb URL (edit mode only).
  const currentImagePreview = removeExistingImage
    ? null
    : newImage
      ? URL.createObjectURL(newImage)
      : initialImageUrl;

  return (
    <Modal
      open={true}
      onClose={props.onClose}
      title={
        isEdit
          ? isDugnad
            ? nb.vedlikehold.editModal.dugnadTitle
            : nb.vedlikehold.editModal.title
          : isDugnad
            ? nb.vedlikehold.addModal.dugnadTitle
            : nb.vedlikehold.addModal.title
      }
    >
      <form className={s.form} onSubmit={handleSubmit} noValidate>
        {!isDugnad && (
          <Field
            label={
              isEdit
                ? nb.vedlikehold.editModal.kategoriLabel
                : nb.vedlikehold.addModal.kategoriLabel
            }
            error={errors.kategori}
          >
            {(wiring) => (
              <div className={s.kategoriRow}>
                <select
                  {...wiring}
                  className={s.select}
                  value={kategoriId}
                  onChange={(e) => setKategoriId(e.target.value)}
                >
                  <option value="">{nb.vedlikehold.addModal.kategoriPlaceholder}</option>
                  {props.kategorier.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
                {!newKategoriOpen && (
                  <button
                    type="button"
                    className={s.inlineButton}
                    onClick={() => setNewKategoriOpen(true)}
                  >
                    {nb.vedlikehold.addModal.newKategori}
                  </button>
                )}
              </div>
            )}
          </Field>
        )}

        {!isDugnad && newKategoriOpen && (
          <div className={s.newKategori}>
            <input
              ref={newKategoriInputRef}
              type="text"
              className={s.input}
              placeholder={nb.vedlikehold.addModal.newKategoriPlaceholder}
              value={newKategoriName}
              maxLength={64}
              onChange={(e) => setNewKategoriName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateNewKategori();
                }
              }}
            />
            <Button
              variant="secondary"
              onClick={handleCreateNewKategori}
              loading={isCreatingKategori}
              type="button"
            >
              {nb.vedlikehold.addModal.newKategoriSubmit}
            </Button>
            <button
              type="button"
              className={s.linkButton}
              onClick={() => {
                setNewKategoriOpen(false);
                setNewKategoriError(null);
              }}
            >
              {nb.common.cancel}
            </button>
            {newKategoriError && (
              <span role="alert" className={s.errorMsg}>
                {newKategoriError}
              </span>
            )}
          </div>
        )}

        <Field
          label={nb.vedlikehold.addModal.descriptionLabel}
          help={nb.vedlikehold.addModal.descriptionHelp}
          error={errors.description}
        >
          {(wiring) => (
            <textarea
              {...wiring}
              className={`${s.input} ${s.textarea}`}
              rows={5}
              maxLength={1024}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>

        <div className={s.imageSection}>
          <span className={s.label}>
            {isEdit ? nb.vedlikehold.editModal.imageLabel : nb.vedlikehold.addModal.imageLabel}
          </span>
          {currentImagePreview && (
            <img src={currentImagePreview} alt="" className={s.imagePreview} />
          )}
          <div className={s.imageActions}>
            <label className={s.uploadButton}>
              <Icon name="upload" size={16} />{' '}
              {isEdit && initialImageUrl && !removeExistingImage
                ? nb.vedlikehold.editModal.imageReplace
                : nb.vedlikehold.addModal.imageUpload}
              <input
                type="file"
                accept="image/jpeg,image/png,image/heic"
                onChange={(e) => void pickImage(e.target.files?.[0] ?? null)}
                className={s.fileInputHidden}
              />
            </label>
            {((isEdit && initialImageUrl) || newImage) && !removeExistingImage && (
              <button
                type="button"
                className={s.linkButton}
                onClick={() => {
                  setNewImage(null);
                  setRemoveExistingImage(Boolean(initialImageUrl));
                }}
              >
                {nb.vedlikehold.addModal.imageRemove}
              </button>
            )}
          </div>
          {isPreparing && (
            <span className={s.imageHint}>{nb.vedlikehold.addModal.imagePreparing}</span>
          )}
        </div>

        {submitError && (
          <p role="alert" className={s.submitError}>
            {submitError}
          </p>
        )}

        <div className={s.actions}>
          <Button variant="secondary" type="button" onClick={props.onClose}>
            {nb.common.cancel}
          </Button>
          <Button variant="primary" type="submit" loading={isSaving} disabled={isPreparing}>
            {isEdit ? nb.vedlikehold.editModal.submit : nb.vedlikehold.addModal.submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
